import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, switchMap, Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api.service';
import { GetEventKpisParams } from 'src/app/models/core/kpis.model';
import { ConsumedProductsResponse } from 'src/app/models/core/consumed-product';

@Injectable({
  providedIn: 'root',
})
export class EventReportApiService {
  private baseUrl = `${environment.api_server}/api/sellers/events`;
  

  constructor(private _http: HttpClient, private _api: ApiService) { }

  getKpis(params: GetEventKpisParams): Observable<any> {
    const { eventId, role, section, extras } = params;
    const url = `${this.baseUrl}/${eventId}/kpis`;

    let httpParams = new HttpParams().set('role', role);
    if (section) {
      httpParams = httpParams.set('section', section);
    }

    if (extras) {
      Object.entries(extras).forEach(([key, value]) => {
        httpParams = httpParams.set(key, value);
      });
    }

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params: httpParams })
      )
    );
  }

  getDemographics(eventId: number | string, role: string): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/demographics`;
    const httpParams = new HttpParams().set('role', role);

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params: httpParams })
      )
    );
  }

  getSoldProductsRanking(
    eventId: number | string,
    role: string,
    page = 1,
    perPage = 10
  ): Observable<any> {
    const url = `${environment.api_server}/api/seller-app/events/${eventId}/ranked-products`;
    const httpParams = new HttpParams()
      .set('role', role)
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params: httpParams })
      )
    );
  }

  getSalesTypeGraphByRange(
    eventId: number | string,
    role: string,
    range: string
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sales-type-table`;
    const params = new HttpParams()
      .set('role', role)
      .set('range', range);

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) => this._http.get(url, { headers, params }))
    );
  }

  getAssistantsOfEvent(
    eventId: number | string,
    page = 1,
    perPage = 10,
    q?: string,
    verified?: boolean | 'all',
    gender?: 'female' | 'male' | 'others' | 'all',
    role?: string
  ): Observable<{ data: any[]; meta: any; links: any }> {
    const url = `${this.baseUrl}/${eventId}/assistants`;

    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (q) {
      httpParams = httpParams.set('q', q);
    }

    if (verified !== undefined && verified !== 'all') {
      httpParams = httpParams.set('verified', String(verified));
    }

    if (gender && gender !== 'all') {
      httpParams = httpParams.set('gender', gender);
    }

    if (role) {
      httpParams = httpParams.set('role', role);
    }

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get<any>(url, { headers, params: httpParams }).pipe(
          map((res) => {
            const { data, meta, links } = res;
            return { data, meta, links };
          })
        )
      )
    );
  }

  getConsumedProductsByAssistant(
    eventId: number | string,
    assistantId: number | string,
    role: string,
    page = 1,
    perPage = 10
  ): Observable<ConsumedProductsResponse> {
    const url = `${this.baseUrl}/${eventId}/assistants/${assistantId}/consumed-products`;

    const params = new HttpParams()
      .set('role', role)
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get<any>(url, { headers, params })
      )
    );
  }


}
